import {ModelCache} from "./model-cache";
import {ModelCacheArray} from "./model-cache-array";
import {Model} from "./model";
import {ModelAdapter} from "./model-adapter"
import {UrlQueryParam} from "./url-query-param";
import {DefaultModelAdapter} from "./default-model-adapter";
import {CmsApiService} from "../cms-api.service";
import {inject, Injectable} from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class ModelStore {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);

  private readonly modelCache: ModelCache;
  private readonly registeredAdapters: Map<string, ModelAdapter<any>>;

  constructor() {
    this.modelCache = new ModelCache();
    this.registeredAdapters = new Map<string, ModelAdapter<any>>();
  }

  public registerModelFor(singularKey: string, pluralKey: string, classInstance: any) {
    this.modelCache.registerCacheFor(singularKey, pluralKey, classInstance);
  }

  public registerAdapterFor<T>(singularKey: string, adapter: ModelAdapter<T>) {
    this.registeredAdapters.set(singularKey, adapter);
  }

  public getRegisteredAdpaterFor<T>(singularKey: string): ModelAdapter<T> | undefined {
    let modelType = this.modelCache.getModelTypeFor(singularKey);

    if (modelType) {
      if (!this.registeredAdapters.has(singularKey)) {
        let adapter: DefaultModelAdapter<T> = new DefaultModelAdapter<T>(singularKey, modelType.pluralKey);
        this.registeredAdapters.set(singularKey, adapter);
      }
    } else {
      console.log("Before registering adaptors, the model should be registered via registerModelFor(singularKey, pluralKey, url);");
    }

    return this.registeredAdapters.get(singularKey);
  }

  public findModel<T extends new (...args: any[]) => any>(modelSingularKey: string, id: string, ...queryParams: UrlQueryParam[]) {
    let modelType = this.modelCache.getModelTypeFor(modelSingularKey);
    let foundModel = this.modelCache.getModel(modelSingularKey, id);
    let foundAdapter = this.getRegisteredAdpaterFor(modelSingularKey);

    console.log('------ found model before cache: ', foundModel);

    if (modelType !== undefined && foundModel === undefined && foundAdapter !== undefined) {
      //Create new, empty model
      foundModel = new modelType.classInstance();
      foundModel.id = id;
      foundModel.status = 'loading';

      //Fetch
      //Be sure to add the loading promise to the returned foundModel
      foundModel.loading = new Promise((resolve, reject) => {
        // @ts-ignore
        this.cms.modelStoreRequest({
          url: foundAdapter.getUrlForId(id, queryParams),
        })
          .then((data) => {
            // @ts-ignore
            let jsonReturn: InstanceType<T> = foundAdapter.fromJsonObject(data);
            // Create a new instance of the expected class and assign the properties from the fetched data to it
            jsonReturn = Object.assign(new modelType.classInstance, jsonReturn);
            this.modelCache.setModel(modelSingularKey, jsonReturn);

            let cachedModel = this.modelCache.getModel(modelSingularKey, id) as InstanceType<T>;
            //keep foundModel the same instance, assign new properties to it
            Object.assign(foundModel, {...cachedModel});

            //perform sideloading
            this.sideLoadRegisteredModels(foundAdapter.generateSimpleJsonApi(data), modelSingularKey);

            foundModel.status = 'loaded';
            // Resolve the promise with the updated object
            resolve(foundModel);
          })
          .catch(() => {
            this.generateErrorResponse(foundModel,
              "503",
              // @ts-ignore
              '503 Service Unavailable',
              'Unable to fetch data from API: \' + modelType.getStandardUrl() + "/" + id'
            );

            reject(foundModel);
          });
      });
    }

    return foundModel;
  }

  private generateErrorResponse(foundModel: any, errorCode: string, errorMessage: string, errorDetail: string) {
    foundModel.status = 'error';

    foundModel.errorMessage = {
      errorCode: errorCode,
      // @ts-ignore
      errorMessage: errorMessage,
      errorDetail: errorDetail
    };
  }

  public findAllModels<T>(modelSingularKey: string, ...queryParams: UrlQueryParam[]) {
    let modelType = this.modelCache.getModelTypeFor(modelSingularKey);
    this.modelCache.clearModels(modelSingularKey);
    let foundModels: ModelCacheArray<T> = new ModelCacheArray<T>();
    let foundAdapter = this.getRegisteredAdpaterFor(modelSingularKey);

    if (modelType && foundAdapter) {
      foundModels.status = 'loading';
      foundModels.loading = new Promise((resolve, reject) => {
        // @ts-ignore
        this.cms.modelStoreRequest({
          url: foundAdapter.getUrlForList(queryParams),
        })
          .then((data) => {
            console.log("ModelStore.findAll -> then: " + modelType?.pluralKey);

            // @ts-ignore
            let jsonReturn: T[] = foundAdapter?.fromJsonList(data);
            jsonReturn.forEach((jsonModel: any) => {
              // Create a new instance of the expected class and assign the properties from the fetched data to it
              let model = Object.assign(new modelType.classInstance, jsonModel);
              this.modelCache.setModel(modelSingularKey, model);

              foundModels.push(model);
            })
            foundModels.status = 'loaded';
            // Resolve the promise with the updated object

            //perform sideloading
            this.sideLoadRegisteredModels(data, modelSingularKey);

            resolve(foundModels);
          })
          .catch((err: Error) => {
            console.log(err);
            this.generateErrorResponse(foundModels,
              "503",
              // @ts-ignore
              '503 Service Unavailable',
              'Unable to fetch data from API: \' + modelType.getStandardUrl() + "/" + id'
            );
            reject(foundModels);
          });
      });
    }

    return foundModels;
  }

  private sideLoadRegisteredModels<T>(data: any, mainSingularKey: string) {
    this.modelCache.getRegisteredCaches().forEach(modelType => {
      if (modelType.singularKey != mainSingularKey) {
        let foundAdapter = this.getRegisteredAdpaterFor(modelType.singularKey);
        // @ts-ignore
        let jsonReturn = foundAdapter.fromJsonList(data);
        jsonReturn.forEach((jsonModel: any) => {
          this.modelCache.setModel(modelType.singularKey, jsonModel);
        });
      }
    });
  }

  public deleteModel<T>(modelSingularKey: string, id: string) {
    let modelType = this.modelCache.getModelTypeFor(modelSingularKey);
    let foundModel = this.modelCache.getModel(modelSingularKey, id);
    let foundAdapter = this.getRegisteredAdpaterFor(modelSingularKey);

    if (foundModel) {
      foundModel.status = 'deleting';
    }

    if (modelType && foundAdapter) {
      foundModel.loading = new Promise((resolve) => {
        this.cms.modelStoreRequest({
          url: foundAdapter.getUrlForId(id, null),
          method: 'DELETE',
        })
          .then(() => {
            console.log("ModelStore.delete -> then: " + modelSingularKey);
            this.modelCache.deleteModel(modelSingularKey, id);
            foundModel = undefined;
            resolve(null);
          });
      });
    }

    return foundModel;
  }

  public createModel<T>({modelSingularKey, record}: { modelSingularKey: string, record: Model }) {
    let modelType = this.modelCache.getModelTypeFor(modelSingularKey);
    let foundAdapter = this.getRegisteredAdpaterFor(modelSingularKey);

    record = Object.assign(new modelType.classInstance, record);
    if (modelType && foundAdapter) {
      record.status = 'inserting';

      record.loading = new Promise((resolve) => {
        this.cms.modelStoreRequest({
          // @ts-ignore
          url: foundAdapter.getUrlForList(),
          method: 'POST',
          body: foundAdapter.toJsonObject(record),
        }).then((data) => {
            // @ts-ignore
            let jsonReturn: InstanceType<T> = foundAdapter.fromJsonObject(data);

            // Create a new instance of the expected class and assign the properties from the fetched data to it
            jsonReturn = Object.assign(new modelType.classInstance, jsonReturn);

            console.log(jsonReturn);
            this.modelCache.setModel(modelSingularKey, jsonReturn);

            // @ts-ignore
            let cachedModel = this.modelCache.getModel(modelSingularKey, jsonReturn.id) as T;
            console.log(record);
            console.log(cachedModel);
            //keep foundModel the same instance, assign new properties to it
            Object.assign(record, {...cachedModel});

            // Resolve the promise with the updated object
            resolve(record);
          })
          .catch((err) => {
            record.status = 'error';
            console.log(err);
          });
      });
    }

    return record;
  }

  public updateModel<T>({modelSingularKey, record}: { modelSingularKey: string, record: Model }) {
    let modelType = this.modelCache.getModelTypeFor(modelSingularKey);
    let foundAdapter = this.getRegisteredAdpaterFor(modelSingularKey);

    if (modelType && foundAdapter) {
      record.status = 'updating';
      record.loading = null;

      record.loading = new Promise((resolve) => {
        this.cms.modelStoreRequest({
          // @ts-ignore
          url: foundAdapter.getUrlForId(record.id),
          method: 'PUT',
          body: foundAdapter.toJsonObject(record),
        })
          .then((data) => {
            console.log("ModelStore.update -> then: " + modelSingularKey);

            // @ts-ignore
            let jsonReturn: InstanceType<T> = foundAdapter.fromJsonObject(data);

            // Create a new instance of the expected class and assign the properties from the fetched data to it
            jsonReturn = Object.assign(new modelType.classInstance, jsonReturn);

            console.log(jsonReturn);
            this.modelCache.setModel(modelSingularKey, jsonReturn);

            let cachedModel = this.modelCache.getModel(modelSingularKey, record.id) as T;
            //keep foundModel the same instance, assign new properties to it
            Object.assign(record, {...cachedModel});

            // Resolve the promise with the updated object
            resolve(record);
          })
          .catch((err) => {
            record.status = 'error';
            console.log(err);
          });
      });
    }

    return record;
  }

  public reloadModel<T extends new (...args: any[]) => any>(modelSingularKey: string, id: string, ...queryParams: UrlQueryParam[]) {
    this.modelCache.deleteModel(modelSingularKey, id);
    return this.findModel(modelSingularKey, id, ...queryParams);
  }
}
